import React from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
interface SessionSummaryProps {
  data: any;
  onFinish: () => void;
}
export const SessionSummary = ({
  data,
  onFinish
}: SessionSummaryProps) => {
  if (!data) return null;
  const formatDate = date => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="flex flex-col h-full" data-id="element-158">
      <div className="p-6" data-id="element-159">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-160">Great work!</h1>
        <p className="text-gray-600 mb-6" data-id="element-161">
          Here's a summary of your session on {formatDate(data.date)}
        </p>
        <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-162">
          <Card className="p-4 text-center" data-id="element-163">
            <h3 className="text-sm text-gray-500 mb-1" data-id="element-164">Total Sets</h3>
            <p className="text-2xl font-bold text-blue-500" data-id="element-165">{data.totalSets}</p>
          </Card>
          <Card className="p-4 text-center" data-id="element-166">
            <h3 className="text-sm text-gray-500 mb-1" data-id="element-167">Total Reps</h3>
            <p className="text-2xl font-bold text-blue-500" data-id="element-168">{data.totalReps}</p>
          </Card>
          {data.totalVolume > 0 && <Card className="p-4 text-center" data-id="element-169">
              <h3 className="text-sm text-gray-500 mb-1" data-id="element-170">Volume</h3>
              <p className="text-2xl font-bold text-blue-500" data-id="element-171">
                {data.totalVolume.toLocaleString()} lb
              </p>
            </Card>}
          {data.totalCardio > 0 && <Card className="p-4 text-center" data-id="element-172">
              <h3 className="text-sm text-gray-500 mb-1" data-id="element-173">Cardio</h3>
              <p className="text-2xl font-bold text-blue-500" data-id="element-174">
                {data.totalCardio}
                {data.exercises.some(ex => ex.isCardio && ex.trackingType === 'calories') ? ' cal' : ' min'}
              </p>
            </Card>}
        </div>
        {/* Achievements section */}
        {data.achievements && data.achievements.length > 0 && <div className="mb-6" data-id="element-175">
            <h2 className="font-bold text-lg mb-3" data-id="element-176">Achievements</h2>
            <Card className="p-4 bg-green-50 border border-green-100" data-id="element-177">
              <div className="space-y-2" data-id="element-178">
                {data.achievements.map((item, index) => <div key={index} className="flex items-center" data-id="element-179">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 text-green-600 flex-shrink-0" data-id="element-180">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-181">
                        <path d="M8 10V20M8 10L4 9.99998V20L8 20M8 10L13.1956 3.93847C13.6886 3.3633 14.4642 3.11604 15.1992 3.29977L15.2467 3.31166C16.5885 3.64711 17.1929 5.21057 16.4258 6.36135L14 9.99998H18.5604C19.8225 9.99998 20.7691 11.1546 20.5216 12.3922L19.3216 18.3922C19.1346 19.3271 18.3138 20 17.3604 20L8 20" data-id="element-182"></path>
                      </svg>
                    </div>
                    <div data-id="element-183">
                      <p className="font-medium text-green-800" data-id="element-184">
                        {item.name}: {item.achievements.join(', ')}
                      </p>
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>}
        <h2 className="font-bold text-lg mb-3" data-id="element-185">Exercise Details</h2>
        <div className="space-y-4 mb-8" data-id="element-186">
          {data.exercises.map((exercise, index) => <Card key={index} className="p-4" data-id="element-187">
              <h3 className="font-medium mb-1" data-id="element-188">{exercise.name}</h3>
              {exercise.equipment && <p className="text-sm text-gray-500 mb-2" data-id="element-189">
                  {exercise.equipment.charAt(0).toUpperCase() + exercise.equipment.slice(1)}
                  {exercise.equipment === 'barbell' && exercise.barWeight && ` (${exercise.barWeight}lb bar)`}
                </p>}
              {exercise.isCardio ? <div className="flex justify-between text-sm" data-id="element-190">
                  <span className="text-gray-500" data-id="element-191">
                    {exercise.trackingType === 'calories' ? 'Calories' : 'Time'}
                    :
                  </span>
                  <span className="font-medium" data-id="element-192">
                    {exercise.sets[0].value}{' '}
                    {exercise.trackingType === 'calories' ? 'cal' : 'min'}
                  </span>
                </div> : <div className="space-y-2" data-id="element-193">
                  {exercise.sets.map((set, setIndex) => <div key={setIndex} className="flex justify-between text-sm" data-id="element-194">
                      <span className="text-gray-500" data-id="element-195">Set {setIndex + 1}:</span>
                      <span className="font-medium" data-id="element-196">
                        {set.reps} reps{' '}
                        {set.weight > 0 ? `at ${set.weight} lb` : ''}
                      </span>
                    </div>)}
                </div>}
              {/* Show achievements for this exercise if any */}
              {exercise.achievements && exercise.achievements.length > 0 && <div className="mt-2 pt-2 border-t border-gray-100" data-id="element-197">
                  <div className="flex" data-id="element-198">
                    <span className="text-xs text-green-600 font-medium mr-1" data-id="element-199">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline mr-1" data-id="element-200">
                        <path d="M8 10V20M8 10L4 9.99998V20L8 20M8 10L13.1956 3.93847C13.6886 3.3633 14.4642 3.11604 15.1992 3.29977L15.2467 3.31166C16.5885 3.64711 17.1929 5.21057 16.4258 6.36135L14 9.99998H18.5604C19.8225 9.99998 20.7691 11.1546 20.5216 12.3922L19.3216 18.3922C19.1346 19.3271 18.3138 20 17.3604 20L8 20" data-id="element-201"></path>
                      </svg>
                    </span>
                    <span className="text-xs text-green-600 font-medium" data-id="element-202">
                      {exercise.achievements.join(', ')}
                    </span>
                  </div>
                </div>}
            </Card>)}
        </div>
      </div>
      <div className="p-4 mt-auto border-t border-gray-100" data-id="element-203">
        <Button onClick={onFinish} variant="primary" fullWidth data-id="element-204">
          Finish
        </Button>
      </div>
    </div>;
};